import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { data, Form, Link, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ASSESSMENT_NAME_SHORT,
	SITE_NAME,
} from '#app/utils/branding.ts'
import { ProviderConnectionForm } from '#app/utils/connections.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/login.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return {}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid username or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return data(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<main
			className="flex min-h-screen flex-col justify-center px-6 py-16 bg-cathedral-page"
		>
			<div className="mx-auto w-full max-w-md rounded-xl border bg-cathedral-card p-8 md:p-10">
				<div className="flex flex-col gap-3 text-center">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.14em] uppercase">
						{SITE_NAME}
					</p>
					<h1 className="cathedral-heading text-cathedral-dark text-4xl font-semibold tracking-wide">
						Welcome Back
					</h1>
					<p className="cathedral-body text-cathedral-brown text-lg">
						Sign in to continue your journey.
					</p>
					{redirectTo ? (
						<p className="cathedral-body text-cathedral-brown text-sm" aria-live="polite">
							You'll continue where you left off after login.
						</p>
					) : null}
				</div>
				<Spacer size="xs" />

				<div>
					<div className="mx-auto w-full max-w-md">
						<Form method="POST" {...getFormProps(form)}>
							<HoneypotInputs />
							<Field
								labelProps={{ children: 'Username', className: 'text-cathedral-brown' }}
								inputProps={{
									...getInputProps(fields.username, { type: 'text' }),
									autoFocus: true,
									className: 'lowercase',
									autoComplete: 'username',
								}}
								errors={fields.username.errors}
							/>

							<Field
								labelProps={{ children: 'Password', className: 'text-cathedral-brown' }}
								inputProps={{
									...getInputProps(fields.password, {
										type: 'password',
									}),
									autoComplete: 'current-password',
								}}
								errors={fields.password.errors}
							/>

							<div className="flex justify-between">
								<CheckboxField
									labelProps={{
										htmlFor: fields.remember.id,
										children: 'Remember me',
										className: 'text-cathedral-brown',
									}}
									buttonProps={getInputProps(fields.remember, {
										type: 'checkbox',
									})}
									errors={fields.remember.errors}
								/>
								<div>
									<Link
										to="/forgot-password"
										className="cathedral-body text-cathedral-gold hover:text-cathedral-gold-light text-xs underline"
									>
										Forgot password?
									</Link>
								</div>
							</div>

							<input
								{...getInputProps(fields.redirectTo, { type: 'hidden' })}
							/>
							<ErrorList errors={form.errors} id={form.errorId} />

							<div className="flex items-center justify-between gap-6 pt-3">
								<StatusButton
									className="w-full border-cathedral-gold !bg-cathedral-surface-strong !text-cathedral-dark hover:!bg-cathedral-surface"
									status={isPending ? 'pending' : (form.status ?? 'idle')}
									type="submit"
									disabled={isPending}
								>
									Sign in
								</StatusButton>
							</div>
						</Form>

						<hr className="my-4 border-bronze-medium" />
						<ul className="flex flex-col gap-3" aria-label="Social login providers">
							<li>
								<ProviderConnectionForm
									type="Login"
									providerName="google"
									redirectTo={redirectTo}
									className="min-h-[44px] rounded-sm border border-cathedral-gold/40 !bg-cathedral-surface-strong px-4 py-3 cathedral-heading text-sm tracking-[0.12em] !text-cathedral-dark uppercase transition-colors hover:border-cathedral-gold hover:!bg-cathedral-surface hover:!text-cathedral-gold"
								/>
							</li>
						</ul>

						<div className="flex items-center justify-center gap-2 pt-6">
							<span className="cathedral-body text-cathedral-brown text-sm">New here?</span>
							<Link
								to={
									redirectTo
										? `/signup?redirectTo=${encodeURIComponent(redirectTo)}`
										: '/signup'
								}
								className="cathedral-body text-cathedral-gold hover:text-cathedral-gold-light text-sm underline"
							>
								Create an account
							</Link>
						</div>
						<p className="cathedral-body text-cathedral-brown pt-2 text-center text-xs">
							View our{' '}
							<Link
								to="/privacy"
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								Privacy Policy
							</Link>{' '}
							and{' '}
							<Link
								to="/tos"
								className="text-cathedral-gold hover:text-cathedral-gold-light underline"
							>
								Terms of Service
							</Link>
							.
						</p>
					</div>
				</div>
			</div>
		</main>
	)
}

export const meta: Route.MetaFunction = () => {
	return [
		{ title: `Log In - ${SITE_NAME}` },
		{
			name: 'description',
			content: `Sign in to continue your ${SITE_NAME} journey and access checkout or ${ASSESSMENT_NAME_SHORT} progress.`,
		},
	]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
